import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, TextField, Grid, useTheme, useMediaQuery, Select, MenuItem, FormControl, InputLabel, CircularProgress } from '@mui/material';
import { showToast } from '../../utils/toastUtils';
import { setUserProfile as setUserProfileAction, setProfileComplete, setCurrentStep } from '../../redux/onboardingReducer';
import { useLocation, useNavigate } from 'react-router-dom';
import OnboardingButtonDock from '../../components/OnboardingButtonDock';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const refreshToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  const response = await fetch(`${API_BASE_URL}/refresh-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) {
    throw new Error('Failed to refresh token');
  }
  const data = await response.json();
  localStorage.setItem('accessToken', data.accessToken);
  return data.accessToken;
};

const apiCall = async (url, options) => {
  let accessToken = localStorage.getItem('accessToken');
  options.headers = {
    ...options.headers,
    'Authorization': `Bearer ${accessToken}`,
  };

  let response = await fetch(url, options);

  if (response.status === 401) {
    try {
      accessToken = await refreshToken();
      options.headers['Authorization'] = `Bearer ${accessToken}`;
      response = await fetch(url, options);
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Redirect to login page
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      throw new Error('Session expired. Please log in again.');
    }
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('API Error response:', errorText);
    throw new Error(`API request failed with status ${response.status}: ${errorText}`);
  }

  return response.json();
};

const updateUserProfile = async (oldUserProfile, newUserProfile) => {
  try {
    const result = await apiCall(`${API_BASE_URL}/user/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        oldUserProfile,
        newUserProfile
      }),
    });
    return result;
  } catch (error) {
    console.error('Error in updateUserProfile:', error);
    throw error;
  }
};

const getUserDetails = async (userId) => {
  const url = `${API_BASE_URL}/user/${userId}`.replace(/([^:]\/)\/+/g, "$1");
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error response:', errorText);
      throw new Error(`API request failed with status ${response.status}: ${errorText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in getUserDetails:', error);
    throw error;
  }
};

const OnboardingProfile = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, organization } = useSelector(state => state.onboarding.profile);
  const userIdFromRedux = useSelector(state => state.onboarding.userId);
  const userId = userIdFromRedux || localStorage.getItem('userId');

  const [allFieldsFilled, setAllFieldsFilled] = useState(false);

  const industries = [
    "Healthcare and Pharmaceuticals",
    "Education and Training",
    "Automobile Dealership",
    "Travel and Tourism",
  ].sort();
  
  const [userProfile, setUserProfileState] = useState(user);
  const [orgProfile, setOrgProfileState] = useState(organization);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    if (userIdFromRedux) {
      localStorage.setItem('userId', userIdFromRedux);
    }
  }, [userIdFromRedux]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoading(true);
      if (userId) {
        try {
          const userData = await getUserDetails(userId);

          if (!userData || typeof userData !== 'object') {
            throw new Error('Invalid user data received');
          }
          if (userData.userId !== userId) {
            throw new Error('Received user data does not match requested userId');
          }

          setUserProfileState({
            userId: userData.userId || '',
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || '',
            phone: userData.phone || ''
          });
          setOrgProfileState({
            organisation: userData.organisation || '',
            industry: userData.industry || '',
            size: userData.size || '',
            website: userData.website || ''
          });
          localStorage.setItem('oldUserData', JSON.stringify(userData));
        } catch (error) {
          console.error('Error fetching user profile:', error);
          showToast(`Failed to load user profile: ${error.message}`, 'error');
        }
      } else {
        setUserProfileState({});
        setOrgProfileState({});
      }
      setLoading(false);
    };

    fetchUserProfile();
  }, [userId]);

  // Add this function to handle organization size change
  const handleSizeChange = (e) => {
    const { name, value } = e.target;
    setOrgProfileState(prev => ({
      ...prev,
      [name]: value
    }));
    checkAllFieldsFilled();
  };

  const checkAllFieldsFilled = () => {
    const userFieldsFilled = Object.values(userProfile).every(field => field !== '' && field !== undefined);
    const orgFieldsFilled = Object.values(orgProfile).every(field => field !== '' && field !== undefined);
    const isComplete = userFieldsFilled && orgFieldsFilled;
    setAllFieldsFilled(isComplete);
    dispatch(setProfileComplete(isComplete));
  };

  const validateEmail = (email) => {
    // RFC 5322 compliant regex
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setUserProfileState(prev => ({ ...prev, [name]: value }));
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
    checkAllFieldsFilled();
  };

  const validatePhone = (phone) => {
    // This regex allows for various phone number formats
    const re = /^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    return re.test(phone);
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    setUserProfileState(prev => ({ ...prev, [name]: value }));
    if (value && !validatePhone(value)) {
      setPhoneError('Please enter a valid phone number');
    } else {
      setPhoneError('');
    }
    checkAllFieldsFilled();
  };

  const validateWebsite = (website) => {
    try {
      new URL(website);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUserProfileState(prev => {
      const newState = { ...prev, [name]: value };
      // Update newUserData in local storage
      const newProfileData = { ...newState, ...orgProfile };
      localStorage.setItem('newUserData', JSON.stringify(newProfileData));
      return newState;
    });
    checkAllFieldsFilled();
  };

  const handleOrgChange = (e) => {
    const { name, value } = e.target;
    setOrgProfileState(prev => {
      const newState = { ...prev, [name]: value };
      // Update newUserData in local storage
      const newProfileData = { ...userProfile, ...newState };
      localStorage.setItem('newUserData', JSON.stringify(newProfileData));
      return newState;
    });
    checkAllFieldsFilled();
  };

  useEffect(() => {
    checkAllFieldsFilled();
  }, [userProfile, orgProfile]);

  // Dispatch setProfileComplete action when component mounts
  useEffect(() => {
    dispatch(setProfileComplete(allFieldsFilled));
  }, [dispatch, allFieldsFilled]);

const handleSubmit = async () => {
  const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'organisation', 'industry', 'size'];
  const unfilledFields = requiredFields.filter(field => !userProfile[field] && !orgProfile[field]);
  const invalidFields = [];
  
  if (userProfile.email && !validateEmail(userProfile.email)) invalidFields.push('email');
  if (orgProfile.website && !validateWebsite(orgProfile.website)) invalidFields.push('website');

    if (unfilledFields.length > 0 || invalidFields.length > 0) {
    let errorMessage = '';
    if (unfilledFields.length > 0) {
      errorMessage += `Please fill in the following fields: ${unfilledFields.join(', ')}. `;
    }
    if (invalidFields.length > 0) {
      errorMessage += `Please enter valid ${invalidFields.join(' and ')}.`;
    }
    showToast(errorMessage, 'error');
    return;
  }

  try {
    setLoading(true);
    const newProfileData = {
      userId: userProfile.userId,
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      email: userProfile.email,
      phone: userProfile.phone,
      organisation: orgProfile.organisation,
      industry: orgProfile.industry,
      size: orgProfile.size,
      website: orgProfile.website
    };

    const oldProfileData = JSON.parse(localStorage.getItem('oldUserData') || '{}');

    // Create an object with only the changed fields
    const changedFields = {};
    Object.keys(newProfileData).forEach(key => {
      if (newProfileData[key] !== oldProfileData[key]) {
        changedFields[key] = newProfileData[key];
      }
    });

    // Check if there are any changes
    if (Object.keys(changedFields).length === 0) {
      showToast('No changes detected. Profile is up to date.', 'info');
      setLoading(false);
      return;
    }

    // Send only changed fields to backend
    const updatedProfile = await updateUserProfile(oldProfileData, changedFields);
    
    if (updatedProfile.message === 'User profile updated successfully') {
      const updatedUserProfile = {
        ...oldProfileData,
        ...changedFields
      };

      // Update Redux store
      const updatedProfileData = {
        user: {
          userId: updatedUserProfile.userId,
          firstName: updatedUserProfile.firstName,
          lastName: updatedUserProfile.lastName,
          email: updatedUserProfile.email,
          phone: updatedUserProfile.phone
        },
        organization: {
          organizationName: updatedUserProfile.organisation,
          industry: updatedUserProfile.industry,
          size: updatedUserProfile.size,
          website: updatedUserProfile.website
        }
      };

      dispatch(setUserProfileAction(updatedProfileData));
      
      // Update local state with the new data
      setUserProfileState(updatedProfileData.user);
      setOrgProfileState({
        organisation: updatedUserProfile.organisation,
        industry: updatedUserProfile.industry,
        size: updatedUserProfile.size,
        website: updatedUserProfile.website
      });
      
      // Update old user data in local storage with new data
      localStorage.setItem('oldUserData', JSON.stringify(updatedUserProfile));
      
      // Clear new user data from local storage
      localStorage.removeItem('newUserData');
      
      showToast('Profile updated successfully.', 'success');

      // Move to the next step
      dispatch(setCurrentStep(1)); // Assuming the next step is Use Case (index 1)
    } else {
      throw new Error('Failed to update profile');
    }
  } catch (error) {
    console.error('Error updating profile:', error);
    showToast('Failed to update profile. Please try again.', 'error');
  } finally {
    setLoading(false);
  }
};


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }} className="onboarding-profile-container fade-in">
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom className="onboarding-profile-title bounce-in">
        Profile Setup
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} className="slide-in-left">
          <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom className="onboarding-profile-section-title">
            User Information
          </Typography>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={userProfile.firstName || ''}
            onChange={handleUserChange}
            margin="normal"
            size={isMobile ? "small" : "medium"}
            className="onboarding-profile-form-field"
          />
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={userProfile.lastName || ''}
            onChange={handleUserChange}
            margin="normal"
            size={isMobile ? "small" : "medium"}
            className="onboarding-profile-form-field"
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={userProfile.email || ''}
            onChange={handleEmailChange}
            margin="normal"
            size={isMobile ? "small" : "medium"}
            error={!!emailError}
            helperText={emailError}
            className="onboarding-profile-form-field"
          />
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={userProfile.phone || ''}
            onChange={handlePhoneChange}
            margin="normal"
            size={isMobile ? "small" : "medium"}
            error={!!phoneError}
            helperText={phoneError || "Enter phone number (e.g., +1 123-456-7890)"}
            className="onboarding-profile-form-field"
          />
        </Grid>
        <Grid item xs={12} md={6} className="slide-in-right">
          <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom className="onboarding-profile-section-title">
            Organization Information
          </Typography>
          <TextField
            fullWidth
            label="Organization Name"
            name="organisation"
            value={orgProfile.organisation || ''}
            onChange={handleOrgChange}
            margin="normal"
            size={isMobile ? "small" : "medium"}
            className="onboarding-profile-form-field"
          />
          <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"} className="onboarding-profile-form-field">
            <InputLabel id="industry-label">Industry</InputLabel>
            <Select
              labelId="industry-label"
              name="industry"
              value={orgProfile.industry || ''}
              onChange={handleOrgChange}
              label="Industry"
              required
            >
              <MenuItem value="" disabled>Select your industry</MenuItem>
              {industries.map((industry, index) => (
                <MenuItem key={index} value={industry}>{industry}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"} className="onboarding-profile-form-field">
            <InputLabel id="size-label">Employee Size</InputLabel>
            <Select
              labelId="size-label"
              name="size"
              value={orgProfile.size || ''}
              onChange={handleOrgChange}
              label="Employee Size"
              required
            >
              <MenuItem value="" disabled>Choose Employee Size</MenuItem>
              <MenuItem value="0-10">0-10</MenuItem>
              <MenuItem value="11-20">11-20</MenuItem>
              <MenuItem value="21-50">21-50</MenuItem>
              <MenuItem value="51-100">51-100</MenuItem>
              <MenuItem value="101-200">101-200</MenuItem>
              <MenuItem value="201-500">201-500</MenuItem>
              <MenuItem value="501-1000">501-1000</MenuItem>
              <MenuItem value="1001-5000">1001-5000</MenuItem>
              <MenuItem value="5001+">5001+</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Website"
            name="website"
            value={orgProfile.website || ''}
            onChange={handleOrgChange}
            margin="normal"
            size={isMobile ? "small" : "medium"}
            placeholder="https://example.com"
            helperText="Please enter a valid URL (e.g., https://example.com)"
            className="onboarding-profile-form-field"
          />
        </Grid>
      </Grid>
      <OnboardingButtonDock
        onSave={handleSubmit}
        onBack={() => {
        }}
        onNext={() => {
       dispatch(setCurrentStep(1));
        }}
        title="save profile"
        loading={loading}
        desktopClassName="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
        mobileClassName="fixed bottom-8 right-8 z-50"
      />
    </Box>
  );
};

export default OnboardingProfile;
